// ./src/components/features/Dashboard/DashboardStats.tsx
import React from "react";
import {
  Paper,
  Title,
  Text,
  Group,
  SimpleGrid,
  ThemeIcon,
  Box,
} from "@mantine/core";

import { AppIcon } from "@/components/generic";
import { DashboardStats as DashboardStatsType } from "@/utils/api";

// Helper to format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

export interface DashboardStatsProps {
  stats: DashboardStatsType;
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({ stats }) => {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
      {/* Total submissions */}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Box>
            <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
              Submissions
            </Text>
            <Title order={2} mt={4}>
              {formatNumber(stats.totalSubmissions)}
            </Title>
            {stats.submissionsChange !== null && (
              <Group gap={4} mt={4}>
                <AppIcon
                  iconName={
                    stats.submissionsChange >= 0
                      ? "TrendingUp2"
                      : "TrendingDown"
                  }
                  size={14}
                  color={stats.submissionsChange >= 0 ? "teal" : "red"}
                />
                <Text
                  size="sm"
                  c={stats.submissionsChange >= 0 ? "teal" : "red"}
                >
                  {stats.submissionsChange > 0 ? "+" : ""}
                  {stats.submissionsChange}% from last month
                </Text>
              </Group>
            )}
          </Box>
          <ThemeIcon size="lg" radius="md" variant="light" color="blue">
            <AppIcon iconName="Inbox" size={20} />
          </ThemeIcon>
        </Group>
      </Paper>

      {/* Success rate */}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Box>
            <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
              Success Rate
            </Text>
            <Title order={2} mt={4}>
              {stats.successRate}%
            </Title>
            <Text size="sm" c="dimmed" mt={4}>
              {formatNumber(stats.successfulSubmissions)} successful
            </Text>
          </Box>
          <ThemeIcon size="lg" radius="md" variant="light" color="green">
            <AppIcon iconName="CircleCheck" size={20} />
          </ThemeIcon>
        </Group>
      </Paper>

      {/* Spam blocked */}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Box>
            <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
              Spam Blocked
            </Text>
            <Title order={2} mt={4}>
              {formatNumber(stats.spamBlocked)}
            </Title>
            <Text size="sm" c="dimmed" mt={4}>
              Protected by honeypot & reCAPTCHA
            </Text>
          </Box>
          <ThemeIcon size="lg" radius="md" variant="light" color="orange">
            <AppIcon iconName="Shield" size={20} />
          </ThemeIcon>
        </Group>
      </Paper>

      {/* Active forms */}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Box>
            <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
              Active Forms
            </Text>
            <Title order={2} mt={4}>
              {stats.activeForms}
            </Title>
            <Text size="sm" c="dimmed" mt={4}>
              {stats.totalForms} total forms
            </Text>
          </Box>
          <ThemeIcon size="lg" radius="md" variant="light" color="violet">
            <AppIcon iconName="FileText" size={20} />
          </ThemeIcon>
        </Group>
      </Paper>
    </SimpleGrid>
  );
};
